import { lazy } from "react";
import { PATHS } from "../constants/paths";
import YogaFacilityGuard from "../guards/YogaFacilityGuard";
import AdminGuard from "../guards/AdminGuard";

const PAGE_404 = lazy(() => import("../components/404"));

// Login

const ROLES = lazy(() => import("../pages/Login/Roles"));

//Profile

const PROFILE_BASIC_DETAILS_PAGE = lazy(
  () => import("../pages/Profile/BasicProfileDetails"),
);
const PROFILE_REPORT_ISSUE_PAGE = lazy(
  () => import("../pages/Profile/ReportIssue"),
);

// Receptionist

const RECEPTIONIST_DASHBOARD = lazy(
  () => import("../pages/Receptionist/Dashboard/Dashboard"),
);
const RECEPTIONIST_APPOINTMENTS = lazy(
  () => import("../pages/Receptionist/Appointments/List"),
);
const RECEPTIONIST_COMPLETE_OPD_PAYMENT = lazy(
  () => import("../pages/Receptionist/Appointments/CompleteOpdPayment"),
);

const RECEPTIONIST_PATIENTS = lazy(
  () => import("../pages/Receptionist/Patients/List"),
);
const ADD_RECEPTIONIST_APPOINTMENT = lazy(
  () => import("../pages/Receptionist/Appointments/Add"),
);
const RECEPTIONIST_BILLINGS = lazy(
  () => import("../pages/Receptionist/Billings/List"),
);
const RECEPTIONIST_VIEW_INVOICE = lazy(
  () => import("../pages/Receptionist/Billings/ViewInvoice"),
);
const RECEPTIONIST_DOCTOR_STATUS = lazy(
  () => import("../pages/Receptionist/Doctor Status/List"),
);
const VIEW_RECEPTIONIST_PATIENT = lazy(
  () => import("../pages/Receptionist/Patients/View"),
);

// Pre OPD Assessments

const PRE_OPD_ASSESSMENTS = lazy(
  () => import("../pages/Pre OPD/Assessments/List"),
);
const PRE_OPD_ASSESSMENTS_ADD = lazy(
  () => import("../pages/Pre OPD/Assessments/Add"),
);
const PRE_OPD_ASSESSMENTS_EDIT = lazy(
  () => import("../pages/Pre OPD/Assessments/Edit"),
);
const PRE_OPD_ASSESSMENTS_VIEW = lazy(
  () => import("../pages/Pre OPD/Assessments/View"),
);

// Follow Up

const FOLLOW_UP_LIST = lazy(() => import("../pages/Follow Up/Follow Up/List"));

// Doctor

const DOCTOR_DASHBOARD = lazy(
  () => import("../pages/Doctor/Dashboard/Dashboard"),
);
const DOCTOR_APPOINTMENTS = lazy(
  () => import("../pages/Doctor/Appointments/List"),
);

const VIEW_DOCTOR_APPOINTMENT = lazy(
  () => import("../pages/Doctor/Appointments/View"),
);
const DOCTOR_PRESCRIPTIONS = lazy(
  () => import("../pages/Doctor/Prescriptions/List"),
);
const VIEW_DOCTOR_PRESCRIPTION = lazy(
  () => import("../pages/Doctor/Prescriptions/View"),
);
const DOCTOR_AI_PRESCRIPTION_REVIEWS = lazy(
  () => import("../pages/Doctor/AiPrescriptionReviews/List"),
);
const VIEW_DOCTOR_AI_PRESCRIPTION = lazy(
  () => import("../pages/Doctor/AiPrescriptionReviews/View"),
);
const EDIT_DOCTOR_AI_PRESCRIPTION = lazy(
  () => import("../pages/Doctor/AiPrescriptionReviews/Edit"),
);
const ADD_DOCTOR_OPD_PRESCRIPTION = lazy(
  () => import("../pages/Doctor/Prescriptions/AddOpdPrescription"),
);
const OPD_PRESCRIPTION_SUMMARY = lazy(
  () => import("../pages/Doctor/Prescriptions/OpdPrescriptionSummary"),
);
const ADD_DOCTOR_IPD_PRESCRIPTION = lazy(
  () => import("../pages/Doctor/Prescriptions/AddIpdPrescription"),
);
const IPD_PRESCRIPTION_SUMMARY = lazy(
  () => import("../pages/Doctor/Prescriptions/IpdPrescriptionSummary"),
);
const EDIT_DOCTOR_OPD_PRESCRIPTION = lazy(
  () => import("../pages/Doctor/Prescriptions/EditOpdPrescription"),
);
const OPD_PRESCRIPTION_EDIT_SUMMARY = lazy(
  () => import("../pages/Doctor/Prescriptions/EditOpdPrescriptionSummary"),
);
const EDIT_DOCTOR_IPD_PRESCRIPTION = lazy(
  () => import("../pages/Doctor/Prescriptions/EditIpdPrescription"),
);
const IPD_PRESCRIPTION_EDIT_SUMMARY = lazy(
  () => import("../pages/Doctor/Prescriptions/EditIpdPrescriptionSummary"),
);
const VIEW_DHP = lazy(() => import("../pages/Doctor/DHP/ViewDhp"));
const DOCTOR_FINANCES = lazy(() => import("../pages/Doctor/Finances/Finance"));
const DOCTOR_PATIENTS = lazy(() => import("../pages/Doctor/Patients/List"));
const VIEW_DOCTOR_PATIENT = lazy(() => import("../pages/Doctor/Patients/View"));
const DOCTOR_MINUTES_USAGE = lazy(
  () => import("../pages/Doctor/MinutesUsage/MinutesUsage"),
);

// Counsellor

const COUNSELLOR_DASHBOARD = lazy(
  () => import("../pages/Counsellor/Dashboard/Dashboard"),
);
const COUNSELLOR_OPD_PATIENTS = lazy(
  () => import("../pages/Counsellor/Opd Patients/List"),
);
const COUNSELLOR_INVOICES = lazy(
  () => import("../pages/Counsellor/Invoices/List"),
);
const COUNSELLOR_TAKE_PAYMENT = lazy(
  () => import("../pages/Counsellor/Invoices/CreateInvoiceSummary"),
);
const COUNSELLOR_PAYMENT_CONFIRM = lazy(
  () => import("../pages/Counsellor/Invoices/PaymentConfirmation"),
);
const COUNSELLOR_VIEW_INVOICE = lazy(
  () => import("../pages/Counsellor/Invoices/ViewInvoice"),
);
const COUNSELLOR_PRINT_INVOICE = lazy(
  () => import("../pages/Counsellor/Invoices/PrintInvoice"),
);
const COUNSELLOR_CREATE_INVOICE = lazy(
  () => import("../pages/Counsellor/Invoices/CreateInvoice"),
);
const COUNSELLOR_OPD_VIEW = lazy(
  () => import("../pages/Counsellor/Opd View/OpdView"),
);
const COUNSELLOR_IPD_VIEW = lazy(
  () => import("../pages/Counsellor/Ipd View/IpdView"),
);
const COUNSELLOR_ITEM_INFO = lazy(
  () => import("../pages/Counsellor/ItemInfo/ItemInfo"),
);

// Nurse

const NURSE_DASHBOARD = lazy(
  () => import("../pages/Nurse/Dashboard/Dashboard"),
);
const NURSE_IPD_PATIENTS = lazy(
  () => import("../pages/Nurse/IPD Patients/List"),
);
const NURSE_IPD_PATIENTS_VIEW = lazy(
  () => import("../pages/Nurse/IPD Patients/View"),
);

// IPD

const IPD_DASHBOARD = lazy(() => import("../pages/IPD/Dashboard/Dashboard"));
const IPD_TRANSFERS = lazy(() => import("../pages/IPD/Transfers/List"));
const ADMIT_IPD_PATIENT = lazy(
  () => import("../pages/IPD/Transfers/AdmitPatient"),
);
const VIEW_IPD_PRESCRIPTION = lazy(
  () => import("../pages/IPD/Transfers/Prescription"),
);
const IPD_TEMPLATES = lazy(() => import("../pages/IPD/Templates/List"));
const IPD_ROOMS = lazy(() => import("../pages/IPD/Rooms/List"));
const IPD_USERS = lazy(() => import("../pages/IPD/Users/List"));
const IPD_PROCEDURES = lazy(() => import("../pages/IPD/Procedures/List"));
const IPD_KNOWLEDGE = lazy(() => import("../pages/IPD/Knowledge/List"));
const VIEW_IPD_KNOWLEDGE = lazy(() => import("../pages/IPD/Knowledge/View"));
const IPD_BILLINGS = lazy(() => import("../pages/IPD/Billings/List"));
const GENERATE_IPD_BILLING = lazy(
  () => import("../pages/IPD/Billings/Generate"),
);
const VIEW_IPD_ROOM = lazy(() => import("../pages/IPD/Rooms/View"));
const ADD_IPD_PROCEDURE = lazy(() => import("../pages/IPD/Procedures/Add"));
const EDIT_IPD_PROCEDURE = lazy(() => import("../pages/IPD/Procedures/Edit"));
const IPD_INVENTORY = lazy(() => import("../pages/IPD/Inventory/List"));
const IPD_INVENTORY_REQUESTS = lazy(
  () => import("../pages/IPD/Inventory/Requests"),
);

// Pharmacist

const PHARMACIST_DASHBOARD = lazy(
  () => import("../pages/Pharmacist/Dashboard/Dashboard"),
);
const PHARMACIST_PATIENTS = lazy(
  () => import("../pages/Pharmacist/Patients List/List"),
);
const PHARMACIST_OTC_INVOICES = lazy(
  () => import("../pages/Pharmacist/OTC Invoices/List"),
);
const PHARMACIST_OTC_VIEW_INVOICE = lazy(
  () => import("../pages/Pharmacist/OTC Invoices/View"),
);
const PHARMACIST_CREATE_OTC_INVOICE = lazy(
  () => import("../pages/Pharmacist/OTC Invoices/Create"),
);
const PHARMACIST_CREATE_PATIENT_INVOICE = lazy(
  () => import("../pages/Pharmacist/Patients List/CreateInvoice"),
);
const PHARMACIST_VIEW_PATIENT_INVOICE = lazy(
  () => import("../pages/Pharmacist/Patients List/ViewPatientInvoice"),
);
const PHARMACIST_INVENTORY = lazy(
  () => import("../pages/Pharmacist/Inventory/List"),
);
const PHARMACIST_INVENTORY_VIEW = lazy(
  () => import("../pages/Pharmacist/Inventory/View"),
);
const PHARMACIST_INVENTORY_BULK_UPDATE = lazy(
  () => import("../pages/Pharmacist/Inventory/Bulk"),
);
const PHARMACIST_ORDERS = lazy(
  () => import("../pages/Pharmacist/Orders/List"),
);
const PHARMACIST_ORDERS_VIEW = lazy(
  () => import("../pages/Pharmacist/Orders/View"),
);
const VIEW_IPD_PROCEDURE = lazy(() => import("../pages/IPD/Procedures/View"));
const VIEW_IPD_USER = lazy(() => import("../pages/IPD/Users/View"));
const DISCHARGE_IPD_USER = lazy(() => import("../pages/IPD/Users/Discharge"));

// Radiologist

const RADIOLOGIST_DASHBOARD = lazy(
  () => import("../pages/Radiologist/Dashboard/Dashboard"),
);
const RADIOLOGIST_BOOKINGS = lazy(
  () => import("../pages/Radiologist/Bookings/List"),
);
const VIEW_RADIOLOGIST_BOOKING = lazy(
  () => import("../pages/Radiologist/Bookings/View"),
);

// Lab Test

const LAB_TEST_DASHBOARD = lazy(
  () => import("../pages/Lab Test/Dashboard/Dashboard"),
);
const LAB_TEST_BOOKINGS = lazy(() => import("../pages/Lab Test/Bookings/List"));
const VIEW_LAB_TEST_BOOKING = lazy(
  () => import("../pages/Lab Test/Bookings/View"),
);
const LAB_TEST_PICKUPS = lazy(() => import("../pages/Lab Test/Pickups/List"));

// Therapist

const THERAPIST_DASHBOARD = lazy(
  () => import("../pages/Therapist/Dashboard/Dashboard"),
);
const THERAPIST_APPOINTMENTS = lazy(
  () => import("../pages/Therapist/Appointments/Appointments"),
);
const THERAPIST_APPOINTMENT_VIEW = lazy(
  () => import("../pages/Therapist/Appointments/View"),
);
const THERAPIST_APPOINTMENT_PRESCRIPTION = lazy(
  () => import("../pages/Therapist/Appointments/Prescription"),
);
const THERAPIST_APPOINTMENT_DHP = lazy(
  () => import("../pages/Therapist/Appointments/Dhp"),
);
const THERAPIST_APPOINTMENT_ADD_THERAPY = lazy(
  () => import("../pages/Therapist/Appointments/AddTherapy"),
);
const THERAPIST_IN_PATIENT_DEPT_LIST = lazy(
  () => import("../pages/Therapist/InPatientDept/List"),
);
const THERAPIST_IN_PATIENT_DEPT_VIEW = lazy(
  () => import("../pages/Therapist/InPatientDept/View"),
);
const THERAPIST_IN_PATIENT_DEPT_DHP = lazy(
  () => import("../pages/Therapist/InPatientDept/Dhp"),
);
const THERAPIST_IN_PATIENT_DEPT_ADD_THERAPY = lazy(
  () => import("../pages/Therapist/InPatientDept/AddTherapy"),
);
const THERAPIST_INVENTORY = lazy(
  () => import("../pages/Therapist/Inventory/List"),
);
const THERAPIST_INVENTORY_REQUESTS = lazy(
  () => import("../pages/Therapist/Inventory/Requests"),
);

// Yoga

const YOGA_DASHBOARD = lazy(() => import("../pages/Yoga/Dashboard/Dashboard"));
const YOGA_BUSINESS_DASHBOARD = lazy(
  () => import("../pages/Yoga/BusinessDashboard/BusinessDashboard"),
);
const YOGA_APPOINTMENTS_LIST = lazy(
  () => import("../pages/Yoga/Appointments/List"),
);
const VIEW_YOGA_APPOINTMENT = lazy(
  () => import("../pages/Yoga/Appointments/View"),
);
const YOGA_APPOINTMENT_PRESCRIPTION = lazy(
  () => import("../pages/Yoga/Appointments/Prescription"),
);
const YOGA_APPOINTMENT_DHP = lazy(
  () => import("../pages/Yoga/Appointments/Dhp"),
);
const YOGA_IN_PATIENT_DEPT_LIST = lazy(
  () => import("../pages/Yoga/InPatientDept/List"),
);
const VIEW_YOGA_IN_PATIENT_DEPT = lazy(
  () => import("../pages/Yoga/InPatientDept/View"),
);
const YOGA_IN_PATIENT_DEPT_DHP = lazy(
  () => import("../pages/Yoga/InPatientDept/Dhp"),
);
const YOGA_PAYMENT_INVOICES = lazy(
  () => import("../pages/Yoga/PaymentInvoices/List"),
);
const YOGA_VIEW_PAYMENT_INVOICE = lazy(
  () => import("../pages/Yoga/PaymentInvoices/ViewInvoice"),
);
const YOGA_PRINT_PAYMENT_INVOICE = lazy(
  () => import("../pages/Yoga/PaymentInvoices/PrintInvoice"),
);
const YOGA_FINANCES = lazy(() => import("../pages/Yoga/Finances/List"));
const YOGA_SESSIONS_LIST = lazy(() => import("../pages/Yoga/Sessions/List"));
const VIEW_YOGA_SESSION_GROUP = lazy(
  () => import("../pages/Yoga/Sessions/ViewGroup"),
);
const VIEW_YOGA_SESSION_ONE_ON_ONE = lazy(
  () => import("../pages/Yoga/Sessions/ViewOneOnOne"),
);
const VIEW_YOGA_SESSION_RECORDED = lazy(
  () => import("../pages/Yoga/Sessions/ViewRecorded"),
);
const YOGA_SESSION_GROUP_FORM = lazy(
  () => import("../pages/Yoga/Sessions/AddGroup"),
);
const YOGA_SESSION_RECORDED_FORM = lazy(
  () => import("../pages/Yoga/Sessions/AddRecorded"),
);
const YOGA_SESSION_ONE_ON_ONE_FORM = lazy(
  () => import("../pages/Yoga/Sessions/AddOneOnOne"),
);
const YOGA_SESSION_GROUP_EDIT_FORM = lazy(
  () => import("../pages/Yoga/Sessions/EditGroup"),
);
const YOGA_SESSION_RECORDED_EDIT_FORM = lazy(
  () => import("../pages/Yoga/Sessions/EditRecorded"),
);
const YOGA_SESSION_ONE_ON_ONE_EDIT_FORM = lazy(
  () => import("../pages/Yoga/Sessions/EditOneOnOne"),
);

// Billing Manager

const BILLING_MANAGER_DASHBOARD = lazy(
  () => import("../pages/Billing Manager/Dashboard/Dashboard"),
);
const BILLING_MANAGER_PROVIDERS = lazy(
  () => import("../pages/Billing Manager/Providers/List"),
);
const VIEW_BILLING_MANAGER_PROVIDER = lazy(
  () => import("../pages/Billing Manager/Providers/View"),
);
const VIEW_BILLING_MANAGER_PROVIDER_INVOICE = lazy(
  () => import("../pages/Billing Manager/Providers/Invoice"),
);
const BILLING_MANAGER_INVOICES = lazy(
  () => import("../pages/Billing Manager/Invoices/List"),
);
const VIEW_BILLING_MANAGER_INVOICE = lazy(
  () => import("../pages/Billing Manager/Invoices/View"),
);
const BILLING_MANAGER_REFUNDS = lazy(
  () => import("../pages/Billing Manager/Refunds/List"),
);
const VIEW_BILLING_MANAGER_REFUND = lazy(
  () => import("../pages/Billing Manager/Refunds/View"),
);
const PAYMENT_BILLING_MANAGER_INVOICE = lazy(
  () => import("../pages/Billing Manager/Invoices/Payment"),
);

// Dietician
const DIETICIAN_DASHBOARD = lazy(
  () => import("../pages/Dietician/Dashboard/Dashboard"),
);
const DIETICIAN_APPOINTMENTS = lazy(
  () => import("../pages/Dietician/Appointments/List"),
);
const VIEW_DIETICIAN_APPOINTMENT = lazy(
  () => import("../pages/Dietician/Appointments/View"),
);
const ADD_DIETICIAN_PRESCRIPTION = lazy(
  () => import("../pages/Dietician/Appointments/AddPrescription"),
);
const VIEW_DIETICIAN_PRESCRIPTION = lazy(
  () => import("../pages/Dietician/Appointments/ViewPrescription"),
);
const DIETICIAN_PLANS = lazy(() => import("../pages/Dietician/Plans/List"));
const VIEW_DIETICIAN_PLAN = lazy(() => import("../pages/Dietician/Plans/View"));
const ADD_DIETICIAN_PLAN = lazy(() => import("../pages/Dietician/Plans/Add"));
const EDIT_DIETICIAN_PLAN = lazy(() => import("../pages/Dietician/Plans/Edit"));
const DIETICIAN_IPDS = lazy(() => import("../pages/Dietician/IPDS/List"));
const DIETICIAN_IPD_VIEW = lazy(() => import("../pages/Dietician/IPDS/View"));
const DIETICIAN_IPD_DHP = lazy(() => import("../pages/Dietician/IPDS/Dhp"));
const DIETICIAN_APPOINTMENT_DHP = lazy(
  () => import("../pages/Dietician/Appointments/Dhp"),
);

// Staff Manager
const STAFF_MANAGER_STAFF_LIST = lazy(
  () => import("../pages/Staff Manager/Staff/List"),
);
const STAFF_MANAGER_STAFF_VIEW = lazy(
  () => import("../pages/Staff Manager/Staff/View"),
);
const STAFF_MANAGER_ADD_STAFF = lazy(
  () => import("../pages/Staff Manager/Staff/Add"),
);
const STAFF_MANAGER_ADD_SLOTS = lazy(
  () => import("../pages/Staff Manager/Staff/AddSlots"),
);
const STAFF_MANAGER_EDIT_SLOTS = lazy(
  () => import("../pages/Staff Manager/Staff/EditSlots"),
);
const STAFF_MANAGER_EDIT_STAFF = lazy(
  () => import("../pages/Staff Manager/Staff/Edit"),
);

// Store Manager
const STORE_MANAGER_DASHBOARD = lazy(
  () => import("../pages/Store Manager/Dashboard/Dashboard"),
);
const STORE_MANAGER_INVENTORY = lazy(
  () => import("../pages/Store Manager/Inventory/List"),
);
const STORE_MANAGER_INVENTORY_BULK_UPDATE = lazy(
  () => import("../pages/Store Manager/Inventory/Bulk"),
);
const VIEW_STORE_MANAGER_INVENTORY = lazy(
  () => import("../pages/Store Manager/Inventory/View"),
);
const STORE_MANAGER_ASSIGNMENT_HISTORY = lazy(
  () => import("../pages/Store Manager/Inventory/Assignment"),
);
const STORE_MANAGER_REQUESTS = lazy(
  () => import("../pages/Store Manager/Requests/List"),
);
const VIEW_STORE_MANAGER_REQUEST = lazy(
  () => import("../pages/Store Manager/Requests/View"),
);
const STORE_MANAGER_PRODUCTS = lazy(
  () => import("../pages/Store Manager/Products/List"),
);
const VIEW_STORE_MANAGER_PRODUCT = lazy(
  () => import("../pages/Store Manager/Products/View"),
);
const ADD_STORE_MANAGER_PRODUCT = lazy(
  () => import("../pages/Store Manager/Products/Add"),
);
const EDIT_STORE_MANAGER_PRODUCT = lazy(
  () => import("../pages/Store Manager/Products/Edit"),
);
const ADD_STORE_MANAGER_BULK_PRODUCT = lazy(
  () => import("../pages/Store Manager/Products/Bulk"),
);
const STORE_MANAGER_SERVICES = lazy(
  () => import("../pages/Store Manager/Services/List"),
);
const VIEW_STORE_MANAGER_SERVICE = lazy(
  () => import("../pages/Store Manager/Services/View"),
);
const ADD_STORE_MANAGER_SERVICE = lazy(
  () => import("../pages/Store Manager/Services/Add"),
);
const EDIT_STORE_MANAGER_SERVICE = lazy(
  () => import("../pages/Store Manager/Services/Edit"),
);
const ADD_STORE_MANAGER_BULK_SERVICE = lazy(
  () => import("../pages/Store Manager/Services/Bulk"),
);

// Admin
const ADMIN_DASHBOARD = lazy(
  () => import("../pages/Admin/Dashboard/Dashboard"),
);
const ADMIN_STAKEHOLDERS = lazy(
  () => import("../pages/Admin/Stakeholders/Stakeholders"),
);
const ADMIN_MINUTES_WALLET = lazy(
  () => import("../pages/Admin/MinutesWallet/MinutesWallet"),
);
const ADMIN_COIN_MANAGEMENT = lazy(
  () => import("../pages/Admin/CoinManagement/CoinManagement"),
);
const ADMIN_COIN_MANAGEMENT_VIEW = lazy(
  () => import("../pages/Admin/CoinManagement/DoctorCoinDetails"),
);

const routes = [
  {
    path: PATHS.PAGE_404,
    key: PATHS.PAGE_404,
    isProtected: false,
    page: <PAGE_404 />,
  },

  // Login

  {
    path: PATHS.ROLES,
    key: PATHS.ROLES,
    isProtected: true,
    pageExternal: true,
    page: <ROLES />,
  },

  // Profile

  {
    path: PATHS.PROFILE_BASIC_DETAILS,
    key: PATHS.PROFILE_BASIC_DETAILS,
    isProtected: true,
    page: <PROFILE_BASIC_DETAILS_PAGE />,
  },
  {
    path: PATHS.PROFILE_REPORT_ISSUE,
    key: PATHS.PROFILE_REPORT_ISSUE,
    isProtected: true,
    page: <PROFILE_REPORT_ISSUE_PAGE />,
  },

  // Receptionist

  {
    path: PATHS.RECEPTIONIST_DASHBOARD,
    key: PATHS.RECEPTIONIST_DASHBOARD,
    isProtected: true,
    page: <RECEPTIONIST_DASHBOARD />,
  },
  {
    path: PATHS.RECEPTIONIST_APPOINTMENTS,
    key: PATHS.RECEPTIONIST_APPOINTMENTS,
    isProtected: true,
    page: <RECEPTIONIST_APPOINTMENTS />,
  },
  {
    path: PATHS.RECEPTIONIST_COMPLETE_OPD_PAYMENT,
    key: PATHS.RECEPTIONIST_COMPLETE_OPD_PAYMENT,
    isProtected: true,
    page: <RECEPTIONIST_COMPLETE_OPD_PAYMENT />,
  },
  {
    path: PATHS.RECEPTIONIST_PATIENTS,
    key: PATHS.RECEPTIONIST_PATIENTS,
    isProtected: true,
    page: <RECEPTIONIST_PATIENTS />,
  },
  {
    path: PATHS.ADD_RECEPTIONIST_APPOINTMENT,
    key: PATHS.ADD_RECEPTIONIST_APPOINTMENT,
    isProtected: true,
    page: <ADD_RECEPTIONIST_APPOINTMENT />,
  },
  {
    path: PATHS.RECEPTIONIST_VIEW_INVOICE,
    key: PATHS.RECEPTIONIST_VIEW_INVOICE,
    isProtected: true,
    page: <RECEPTIONIST_VIEW_INVOICE />,
  },
  {
    path: PATHS.RECEPTIONIST_BILLINGS,
    key: PATHS.RECEPTIONIST_BILLINGS,
    isProtected: true,
    page: <RECEPTIONIST_BILLINGS />,
  },
  {
    path: PATHS.RECEPTIONIST_DOCTOR_STATUS,
    key: PATHS.RECEPTIONIST_DOCTOR_STATUS,
    isProtected: true,
    page: <RECEPTIONIST_DOCTOR_STATUS />,
  },
  {
    path: PATHS.VIEW_RECEPTIONIST_PATIENT,
    key: PATHS.VIEW_RECEPTIONIST_PATIENT,
    isProtected: true,
    page: <VIEW_RECEPTIONIST_PATIENT />,
  },

  // Pre OPD Assessments

  {
    path: PATHS.PRE_OPD_ASSESSMENTS,
    key: PATHS.PRE_OPD_ASSESSMENTS,
    isProtected: true,
    page: <PRE_OPD_ASSESSMENTS />,
  },
  {
    path: PATHS.PRE_OPD_ASSESSMENTS_ADD,
    key: PATHS.PRE_OPD_ASSESSMENTS_ADD,
    isProtected: true,
    page: <PRE_OPD_ASSESSMENTS_ADD />,
  },
  {
    path: PATHS.PRE_OPD_ASSESSMENTS_EDIT,
    key: PATHS.PRE_OPD_ASSESSMENTS_EDIT,
    isProtected: true,
    page: <PRE_OPD_ASSESSMENTS_EDIT />,
  },
  {
    path: PATHS.PRE_OPD_ASSESSMENTS_VIEW,
    key: PATHS.PRE_OPD_ASSESSMENTS_VIEW,
    isProtected: true,
    page: <PRE_OPD_ASSESSMENTS_VIEW />,
  },

  // Follow Up

  {
    path: PATHS.FOLLOW_UP_LIST,
    key: PATHS.FOLLOW_UP_LIST,
    isProtected: true,
    page: <FOLLOW_UP_LIST />,
  },

  // Doctor

  {
    path: PATHS.DOCTOR_DASHBOARD,
    key: PATHS.DOCTOR_DASHBOARD,
    isProtected: true,
    page: <DOCTOR_DASHBOARD />,
  },
  {
    path: PATHS.DOCTOR_APPOINTMENTS,
    key: PATHS.DOCTOR_APPOINTMENTS,
    isProtected: true,
    page: <DOCTOR_APPOINTMENTS />,
  },
  {
    path: PATHS.VIEW_DOCTOR_APPOINTMENT,
    key: PATHS.VIEW_DOCTOR_APPOINTMENT,
    isProtected: true,
    page: <VIEW_DOCTOR_APPOINTMENT />,
  },
  {
    path: PATHS.DOCTOR_PRESCRIPTIONS,
    key: PATHS.DOCTOR_PRESCRIPTIONS,
    isProtected: true,
    page: <DOCTOR_PRESCRIPTIONS />,
  },
  {
    path: PATHS.VIEW_DOCTOR_PRESCRIPTION,
    key: PATHS.VIEW_DOCTOR_PRESCRIPTION,
    isProtected: true,
    page: <VIEW_DOCTOR_PRESCRIPTION />,
  },
  {
    path: PATHS.DOCTOR_AI_PRESCRIPTION_REVIEWS,
    key: PATHS.DOCTOR_AI_PRESCRIPTION_REVIEWS,
    isProtected: true,
    page: <DOCTOR_AI_PRESCRIPTION_REVIEWS />,
  },
  {
    path: PATHS.VIEW_DOCTOR_AI_PRESCRIPTION,
    key: PATHS.VIEW_DOCTOR_AI_PRESCRIPTION,
    isProtected: true,
    page: <VIEW_DOCTOR_AI_PRESCRIPTION />,
  },
  {
    path: PATHS.EDIT_DOCTOR_AI_PRESCRIPTION,
    key: PATHS.EDIT_DOCTOR_AI_PRESCRIPTION,
    isProtected: true,
    page: <EDIT_DOCTOR_AI_PRESCRIPTION />,
  },
  {
    path: PATHS.ADD_DOCTOR_OPD_PRESCRIPTION,
    key: PATHS.ADD_DOCTOR_OPD_PRESCRIPTION,
    isProtected: true,
    page: <ADD_DOCTOR_OPD_PRESCRIPTION />,
  },
  {
    path: PATHS.OPD_PRESCRIPTION_SUMMARY,
    key: PATHS.OPD_PRESCRIPTION_SUMMARY,
    isProtected: true,
    page: <OPD_PRESCRIPTION_SUMMARY />,
  },
  {
    path: PATHS.ADD_DOCTOR_IPD_PRESCRIPTION,
    key: PATHS.ADD_DOCTOR_IPD_PRESCRIPTION,
    isProtected: true,
    page: <ADD_DOCTOR_IPD_PRESCRIPTION />,
  },
  {
    path: PATHS.IPD_PRESCRIPTION_SUMMARY,
    key: PATHS.IPD_PRESCRIPTION_SUMMARY,
    isProtected: true,
    page: <IPD_PRESCRIPTION_SUMMARY />,
  },
  {
    path: PATHS.EDIT_DOCTOR_OPD_PRESCRIPTION,
    key: PATHS.EDIT_DOCTOR_OPD_PRESCRIPTION,
    isProtected: true,
    page: <EDIT_DOCTOR_OPD_PRESCRIPTION />,
  },
  {
    path: PATHS.OPD_PRESCRIPTION_EDIT_SUMMARY,
    key: PATHS.OPD_PRESCRIPTION_EDIT_SUMMARY,
    isProtected: true,
    page: <OPD_PRESCRIPTION_EDIT_SUMMARY />,
  },
  {
    path: PATHS.EDIT_DOCTOR_IPD_PRESCRIPTION,
    key: PATHS.EDIT_DOCTOR_IPD_PRESCRIPTION,
    isProtected: true,
    page: <EDIT_DOCTOR_IPD_PRESCRIPTION />,
  },
  {
    path: PATHS.IPD_PRESCRIPTION_EDIT_SUMMARY,
    key: PATHS.IPD_PRESCRIPTION_EDIT_SUMMARY,
    isProtected: true,
    page: <IPD_PRESCRIPTION_EDIT_SUMMARY />,
  },
  {
    path: PATHS.VIEW_DHP,
    key: PATHS.VIEW_DHP,
    isProtected: true,
    page: <VIEW_DHP />,
  },
  {
    path: PATHS.DOCTOR_FINANCES,
    key: PATHS.DOCTOR_FINANCES,
    isProtected: true,
    page: <DOCTOR_FINANCES />,
  },
  {
    path: PATHS.DOCTOR_PATIENTS,
    key: PATHS.DOCTOR_PATIENTS,
    isProtected: true,
    page: <DOCTOR_PATIENTS />,
  },
  {
    path: PATHS.VIEW_DOCTOR_PATIENT,
    key: PATHS.VIEW_DOCTOR_PATIENT,
    isProtected: true,
    page: <VIEW_DOCTOR_PATIENT />,
  },
  {
    path: PATHS.DOCTOR_MINUTES_USAGE,
    key: PATHS.DOCTOR_MINUTES_USAGE,
    isProtected: true,
    page: <DOCTOR_MINUTES_USAGE />,
  },

  // Counsellor

  {
    path: PATHS.COUNSELLOR_DASHBOARD,
    key: PATHS.COUNSELLOR_DASHBOARD,
    isProtected: true,
    page: <COUNSELLOR_DASHBOARD />,
  },
  {
    path: PATHS.COUNSELLOR_OPD_PATIENTS,
    key: PATHS.COUNSELLOR_OPD_PATIENTS,
    isProtected: true,
    page: <COUNSELLOR_OPD_PATIENTS />,
  },
  {
    path: PATHS.COUNSELLOR_INVOICES,
    key: PATHS.COUNSELLOR_INVOICES,
    isProtected: true,
    page: <COUNSELLOR_INVOICES />,
  },
  {
    path: PATHS.COUNSELLOR_TAKE_PAYMENT,
    key: PATHS.COUNSELLOR_TAKE_PAYMENT,
    isProtected: true,
    page: <COUNSELLOR_TAKE_PAYMENT />,
  },
  {
    path: PATHS.COUNSELLOR_PAYMENT_CONFIRM,
    key: PATHS.COUNSELLOR_PAYMENT_CONFIRM,
    isProtected: true,
    page: <COUNSELLOR_PAYMENT_CONFIRM />,
  },
  {
    path: PATHS.COUNSELLOR_VIEW_INVOICE,
    key: PATHS.COUNSELLOR_VIEW_INVOICE,
    isProtected: true,
    page: <COUNSELLOR_VIEW_INVOICE />,
  },
  {
    path: PATHS.COUNSELLOR_PRINT_INVOICE,
    key: PATHS.COUNSELLOR_PRINT_INVOICE,
    isProtected: true,
    page: <COUNSELLOR_PRINT_INVOICE />,
  },
  {
    path: PATHS.COUNSELLOR_CREATE_INVOICE,
    key: PATHS.COUNSELLOR_CREATE_INVOICE,
    isProtected: true,
    page: <COUNSELLOR_CREATE_INVOICE />,
  },
  {
    path: PATHS.COUNSELLOR_OPD_VIEW,
    key: PATHS.COUNSELLOR_OPD_VIEW,
    isProtected: true,
    page: <COUNSELLOR_OPD_VIEW />,
  },
  {
    path: PATHS.COUNSELLOR_IPD_VIEW,
    key: PATHS.COUNSELLOR_IPD_VIEW,
    isProtected: true,
    page: <COUNSELLOR_IPD_VIEW />,
  },
  {
    path: PATHS.COUNSELLOR_ITEM_INFO,
    key: PATHS.COUNSELLOR_ITEM_INFO,
    isProtected: true,
    page: <COUNSELLOR_ITEM_INFO />,
  },
  {
    path: PATHS.NURSE_DASHBOARD,
    key: PATHS.NURSE_DASHBOARD,
    isProtected: true,
    page: <NURSE_DASHBOARD />,
  },
  {
    path: PATHS.NURSE_IPD_PATIENTS,
    key: PATHS.NURSE_IPD_PATIENTS,
    isProtected: true,
    page: <NURSE_IPD_PATIENTS />,
  },
  {
    path: PATHS.NURSE_IPD_PATIENTS_VIEW,
    key: PATHS.NURSE_IPD_PATIENTS_VIEW,
    isProtected: true,
    page: <NURSE_IPD_PATIENTS_VIEW />,
  },

  // IPD

  {
    path: PATHS.IPD_DASHBOARD,
    key: PATHS.IPD_DASHBOARD,
    isProtected: true,
    page: <IPD_DASHBOARD />,
  },
  {
    path: PATHS.IPD_TRANSFERS,
    key: PATHS.IPD_TRANSFERS,
    isProtected: true,
    page: <IPD_TRANSFERS />,
  },
  {
    path: PATHS.ADMIT_IPD_PATIENT,
    key: PATHS.ADMIT_IPD_PATIENT,
    isProtected: true,
    page: <ADMIT_IPD_PATIENT />,
  },
  {
    path: PATHS.VIEW_IPD_PRESCRIPTION,
    key: PATHS.VIEW_IPD_PRESCRIPTION,
    isProtected: true,
    page: <VIEW_IPD_PRESCRIPTION />,
  },
  {
    path: PATHS.IPD_TEMPLATES,
    key: PATHS.IPD_TEMPLATES,
    isProtected: true,
    page: <IPD_TEMPLATES />,
  },
  {
    path: PATHS.IPD_ROOMS,
    key: PATHS.IPD_ROOMS,
    isProtected: true,
    page: <IPD_ROOMS />,
  },
  {
    path: PATHS.IPD_USERS,
    key: PATHS.IPD_USERS,
    isProtected: true,
    page: <IPD_USERS />,
  },
  {
    path: PATHS.IPD_PROCEDURES,
    key: PATHS.IPD_PROCEDURES,
    isProtected: true,
    page: <IPD_PROCEDURES />,
  },
  {
    path: PATHS.IPD_KNOWLEDGE,
    key: PATHS.IPD_KNOWLEDGE,
    isProtected: true,
    page: <IPD_KNOWLEDGE />,
  },
  {
    path: PATHS.VIEW_IPD_KNOWLEDGE,
    key: PATHS.VIEW_IPD_KNOWLEDGE,
    isProtected: true,
    page: <VIEW_IPD_KNOWLEDGE />,
  },
  {
    path: PATHS.IPD_BILLINGS,
    key: PATHS.IPD_BILLINGS,
    isProtected: true,
    page: <IPD_BILLINGS />,
  },
  {
    path: PATHS.GENERATE_IPD_BILLING,
    key: PATHS.GENERATE_IPD_BILLING,
    isProtected: true,
    page: <GENERATE_IPD_BILLING />,
  },
  {
    path: PATHS.VIEW_IPD_ROOM,
    key: PATHS.VIEW_IPD_ROOM,
    isProtected: true,
    page: <VIEW_IPD_ROOM />,
  },
  {
    path: PATHS.ADD_IPD_PROCEDURE,
    key: PATHS.ADD_IPD_PROCEDURE,
    isProtected: true,
    page: <ADD_IPD_PROCEDURE />,
  },
  {
    path: PATHS.EDIT_IPD_PROCEDURE,
    key: PATHS.EDIT_IPD_PROCEDURE,
    isProtected: true,
    page: <EDIT_IPD_PROCEDURE />,
  },
  {
    path: PATHS.IPD_INVENTORY,
    key: PATHS.IPD_INVENTORY,
    isProtected: true,
    page: <IPD_INVENTORY />,
  },
  {
    path: PATHS.IPD_INVENTORY_REQUESTS,
    key: PATHS.IPD_INVENTORY_REQUESTS,
    isProtected: true,
    page: <IPD_INVENTORY_REQUESTS />,
  },

  // Pharmacist

  {
    path: PATHS.PHARMACIST_DASHBOARD,
    key: PATHS.PHARMACIST_DASHBOARD,
    isProtected: true,
    page: <PHARMACIST_DASHBOARD />,
  },
  {
    path: PATHS.PHARMACIST_PATIENTS,
    key: PATHS.PHARMACIST_PATIENTS,
    isProtected: true,
    page: <PHARMACIST_PATIENTS />,
  },
  {
    path: PATHS.PHARMACIST_OTC_INVOICES,
    key: PATHS.PHARMACIST_OTC_INVOICES,
    isProtected: true,
    page: <PHARMACIST_OTC_INVOICES />,
  },
  {
    path: PATHS.PHARMACIST_CREATE_OTC_INVOICE,
    key: PATHS.PHARMACIST_CREATE_OTC_INVOICE,
    isProtected: true,
    page: <PHARMACIST_CREATE_OTC_INVOICE />,
  },
  {
    path: PATHS.PHARMACIST_OTC_VIEW_INVOICE,
    key: PATHS.PHARMACIST_OTC_VIEW_INVOICE,
    isProtected: true,
    page: <PHARMACIST_OTC_VIEW_INVOICE />,
  },
  {
    path: PATHS.PHARMACIST_VIEW_PATIENT_INVOICE,
    key: PATHS.PHARMACIST_VIEW_PATIENT_INVOICE,
    isProtected: true,
    page: <PHARMACIST_VIEW_PATIENT_INVOICE />,
  },
  {
    path: PATHS.PHARMACIST_CREATE_PATIENT_INVOICE,
    key: PATHS.PHARMACIST_CREATE_PATIENT_INVOICE,
    isProtected: true,
    page: <PHARMACIST_CREATE_PATIENT_INVOICE />,
  },
  {
    path: PATHS.PHARMACIST_INVENTORY,
    key: PATHS.PHARMACIST_INVENTORY,
    isProtected: true,
    page: <PHARMACIST_INVENTORY />,
  },
  {
    path: PATHS.PHARMACIST_INVENTORY_VIEW,
    key: PATHS.PHARMACIST_INVENTORY_VIEW,
    isProtected: true,
    page: <PHARMACIST_INVENTORY_VIEW />,
  },
  {
    path: PATHS.PHARMACIST_INVENTORY_BULK_UPDATE,
    key: PATHS.PHARMACIST_INVENTORY_BULK_UPDATE,
    isProtected: true,
    page: <PHARMACIST_INVENTORY_BULK_UPDATE />,
  },
  {
    path: PATHS.PHARMACIST_ORDERS,
    key: PATHS.PHARMACIST_ORDERS,
    isProtected: true,
    page: <PHARMACIST_ORDERS />,
  },
  {
    path: PATHS.PHARMACIST_ORDERS_VIEW,
    key: PATHS.PHARMACIST_ORDERS_VIEW,
    isProtected: true,
    page: <PHARMACIST_ORDERS_VIEW />,
  },
  {
    path: PATHS.VIEW_IPD_PROCEDURE,
    key: PATHS.VIEW_IPD_PROCEDURE,
    isProtected: true,
    page: <VIEW_IPD_PROCEDURE />,
  },
  {
    path: PATHS.VIEW_IPD_USER,
    key: PATHS.VIEW_IPD_USER,
    isProtected: true,
    page: <VIEW_IPD_USER />,
  },
  {
    path: PATHS.DISCHARGE_IPD_USER,
    key: PATHS.DISCHARGE_IPD_USER,
    isProtected: true,
    page: <DISCHARGE_IPD_USER />,
  },

  // Radiologist

  {
    path: PATHS.RADIOLOGIST_DASHBOARD,
    key: PATHS.RADIOLOGIST_DASHBOARD,
    isProtected: true,
    page: <RADIOLOGIST_DASHBOARD />,
  },
  {
    path: PATHS.RADIOLOGIST_BOOKINGS,
    key: PATHS.RADIOLOGIST_BOOKINGS,
    isProtected: true,
    page: <RADIOLOGIST_BOOKINGS />,
  },
  {
    path: PATHS.VIEW_RADIOLOGIST_BOOKING,
    key: PATHS.VIEW_RADIOLOGIST_BOOKING,
    isProtected: true,
    page: <VIEW_RADIOLOGIST_BOOKING />,
  },

  // Lab Test

  {
    path: PATHS.LAB_TEST_DASHBOARD,
    key: PATHS.LAB_TEST_DASHBOARD,
    isProtected: true,
    page: <LAB_TEST_DASHBOARD />,
  },
  {
    path: PATHS.LAB_TEST_BOOKINGS,
    key: PATHS.LAB_TEST_BOOKINGS,
    isProtected: true,
    page: <LAB_TEST_BOOKINGS />,
  },
  {
    path: PATHS.VIEW_LAB_TEST_BOOKING,
    key: PATHS.VIEW_LAB_TEST_BOOKING,
    isProtected: true,
    page: <VIEW_LAB_TEST_BOOKING />,
  },
  {
    path: PATHS.LAB_TEST_PICKUPS,
    key: PATHS.LAB_TEST_PICKUPS,
    isProtected: true,
    page: <LAB_TEST_PICKUPS />,
  },

  // Therapist

  {
    path: PATHS.THERAPIST_DASHBOARD,
    key: PATHS.THERAPIST_DASHBOARD,
    isProtected: true,
    page: <THERAPIST_DASHBOARD />,
  },
  {
    path: PATHS.THERAPIST_APPOINTMENTS,
    key: PATHS.THERAPIST_APPOINTMENTS,
    isProtected: true,
    page: <THERAPIST_APPOINTMENTS />,
  },
  {
    path: PATHS.THERAPIST_APPOINTMENT_PRESCRIPTION,
    key: PATHS.THERAPIST_APPOINTMENT_PRESCRIPTION,
    isProtected: true,
    page: <THERAPIST_APPOINTMENT_PRESCRIPTION />,
  },
  {
    path: PATHS.THERAPIST_APPOINTMENT_DHP,
    key: PATHS.THERAPIST_APPOINTMENT_DHP,
    isProtected: true,
    page: <THERAPIST_APPOINTMENT_DHP />,
  },
  {
    path: PATHS.THERAPIST_APPOINTMENT_ADD_THERAPY,
    key: PATHS.THERAPIST_APPOINTMENT_ADD_THERAPY,
    isProtected: true,
    page: <THERAPIST_APPOINTMENT_ADD_THERAPY />,
  },
  {
    path: PATHS.THERAPIST_APPOINTMENT_VIEW,
    key: PATHS.THERAPIST_APPOINTMENT_VIEW,
    isProtected: true,
    page: <THERAPIST_APPOINTMENT_VIEW />,
  },
  {
    path: PATHS.THERAPIST_IN_PATIENT_DEPT_ADD_THERAPY,
    key: PATHS.THERAPIST_IN_PATIENT_DEPT_ADD_THERAPY,
    isProtected: true,
    page: <THERAPIST_IN_PATIENT_DEPT_ADD_THERAPY />,
  },
  {
    path: PATHS.THERAPIST_IN_PATIENT_DEPT_DHP,
    key: PATHS.THERAPIST_IN_PATIENT_DEPT_DHP,
    isProtected: true,
    page: <THERAPIST_IN_PATIENT_DEPT_DHP />,
  },
  {
    path: PATHS.THERAPIST_IN_PATIENT_DEPT_VIEW,
    key: PATHS.THERAPIST_IN_PATIENT_DEPT_VIEW,
    isProtected: true,
    page: <THERAPIST_IN_PATIENT_DEPT_VIEW />,
  },
  {
    path: PATHS.THERAPIST_IN_PATIENT_DEPT,
    key: PATHS.THERAPIST_IN_PATIENT_DEPT,
    isProtected: true,
    page: <THERAPIST_IN_PATIENT_DEPT_LIST />,
  },
  {
    path: PATHS.THERAPIST_INVENTORY,
    key: PATHS.THERAPIST_INVENTORY,
    isProtected: true,
    page: <THERAPIST_INVENTORY />,
  },
  {
    path: PATHS.THERAPIST_INVENTORY_REQUESTS,
    key: PATHS.THERAPIST_INVENTORY_REQUESTS,
    isProtected: true,
    page: <THERAPIST_INVENTORY_REQUESTS />,
  },

  // Yoga

  {
    path: PATHS.YOGA_DASHBOARD,
    key: PATHS.YOGA_DASHBOARD,
    isProtected: true,
    page: <YOGA_DASHBOARD />,
  },
  {
    path: PATHS.YOGA_BUSINESS_DASHBOARD,
    key: PATHS.YOGA_BUSINESS_DASHBOARD,
    isProtected: true,
    page: (
      <YogaFacilityGuard>
        <YOGA_BUSINESS_DASHBOARD />
      </YogaFacilityGuard>
    ),
  },
  {
    path: PATHS.YOGA_APPOINTMENTS,
    key: PATHS.YOGA_APPOINTMENTS,
    isProtected: true,
    page: <YOGA_APPOINTMENTS_LIST />,
  },
  {
    path: PATHS.YOGA_APPOINTMENT_PRESCRIPTION,
    key: PATHS.YOGA_APPOINTMENT_PRESCRIPTION,
    isProtected: true,
    page: <YOGA_APPOINTMENT_PRESCRIPTION />,
  },
  {
    path: PATHS.YOGA_APPOINTMENT_DHP,
    key: PATHS.YOGA_APPOINTMENT_DHP,
    isProtected: true,
    page: <YOGA_APPOINTMENT_DHP />,
  },
  {
    path: PATHS.VIEW_YOGA_APPOINTMENT,
    key: PATHS.VIEW_YOGA_APPOINTMENT,
    isProtected: true,
    page: <VIEW_YOGA_APPOINTMENT />,
  },
  {
    path: PATHS.YOGA_IN_PATIENT_DEPT,
    key: PATHS.YOGA_IN_PATIENT_DEPT,
    isProtected: true,
    page: (
      <YogaFacilityGuard>
        <YOGA_IN_PATIENT_DEPT_LIST />
      </YogaFacilityGuard>
    ),
  },
  {
    path: PATHS.VIEW_YOGA_IN_PATIENT_DEPT,
    key: PATHS.VIEW_YOGA_IN_PATIENT_DEPT,
    isProtected: true,
    page: (
      <YogaFacilityGuard>
        <VIEW_YOGA_IN_PATIENT_DEPT />
      </YogaFacilityGuard>
    ),
  },
  {
    path: PATHS.YOGA_IN_PATIENT_DEPT_DHP,
    key: PATHS.YOGA_IN_PATIENT_DEPT_DHP,
    isProtected: true,
    page: (
      <YogaFacilityGuard>
        <YOGA_IN_PATIENT_DEPT_DHP />
      </YogaFacilityGuard>
    ),
  },
  {
    path: PATHS.YOGA_PAYMENT_INVOICES,
    key: PATHS.YOGA_PAYMENT_INVOICES,
    isProtected: true,
    page: <YOGA_PAYMENT_INVOICES />,
  },
  {
    path: PATHS.YOGA_PRINT_PAYMENT_INVOICE,
    key: PATHS.YOGA_PRINT_PAYMENT_INVOICE,
    isProtected: true,
    page: <YOGA_PRINT_PAYMENT_INVOICE />,
  },
  {
    path: PATHS.YOGA_VIEW_PAYMENT_INVOICE,
    key: PATHS.YOGA_VIEW_PAYMENT_INVOICE,
    isProtected: true,
    page: <YOGA_VIEW_PAYMENT_INVOICE />,
  },
  {
    path: PATHS.YOGA_FINANCES,
    key: PATHS.YOGA_FINANCES,
    isProtected: true,
    page: (
      <YogaFacilityGuard>
        <YOGA_FINANCES />
      </YogaFacilityGuard>
    ),
  },
  {
    path: PATHS.YOGA_SESSIONS,
    key: PATHS.YOGA_SESSIONS,
    isProtected: true,
    page: <YOGA_SESSIONS_LIST />,
  },
  {
    path: PATHS.VIEW_YOGA_SESSION_GROUP,
    key: PATHS.VIEW_YOGA_SESSION_GROUP,
    isProtected: true,
    page: <VIEW_YOGA_SESSION_GROUP />,
  },
  {
    path: PATHS.VIEW_YOGA_SESSION_ONE_ON_ONE,
    key: PATHS.VIEW_YOGA_SESSION_ONE_ON_ONE,
    isProtected: true,
    page: <VIEW_YOGA_SESSION_ONE_ON_ONE />,
  },
  {
    path: PATHS.VIEW_YOGA_SESSION_RECORDED,
    key: PATHS.VIEW_YOGA_SESSION_RECORDED,
    isProtected: true,
    page: <VIEW_YOGA_SESSION_RECORDED />,
  },
  {
    path: PATHS.YOGA_SESSION_GROUP_FORM,
    key: PATHS.YOGA_SESSION_GROUP_FORM,
    isProtected: true,
    page: <YOGA_SESSION_GROUP_FORM />,
  },
  {
    path: PATHS.YOGA_SESSION_RECORDED_FORM,
    key: PATHS.YOGA_SESSION_RECORDED_FORM,
    isProtected: true,
    page: <YOGA_SESSION_RECORDED_FORM />,
  },
  {
    path: PATHS.YOGA_SESSION_ONE_ON_ONE_FORM,
    key: PATHS.YOGA_SESSION_ONE_ON_ONE_FORM,
    isProtected: true,
    page: <YOGA_SESSION_ONE_ON_ONE_FORM />,
  },
  {
    path: PATHS.YOGA_SESSION_GROUP_EDIT_FORM,
    key: PATHS.YOGA_SESSION_GROUP_EDIT_FORM,
    isProtected: true,
    page: <YOGA_SESSION_GROUP_EDIT_FORM />,
  },
  {
    path: PATHS.YOGA_SESSION_RECORDED_EDIT_FORM,
    key: PATHS.YOGA_SESSION_RECORDED_EDIT_FORM,
    isProtected: true,
    page: <YOGA_SESSION_RECORDED_EDIT_FORM />,
  },
  {
    path: PATHS.YOGA_SESSION_ONE_ON_ONE_EDIT_FORM,
    key: PATHS.YOGA_SESSION_ONE_ON_ONE_EDIT_FORM,
    isProtected: true,
    page: <YOGA_SESSION_ONE_ON_ONE_EDIT_FORM />,
  },

  // Billing Manager

  {
    path: PATHS.BILLING_MANAGER_DASHBOARD,
    key: PATHS.BILLING_MANAGER_DASHBOARD,
    isProtected: true,
    page: <BILLING_MANAGER_DASHBOARD />,
  },
  {
    path: PATHS.BILLING_MANAGER_PROVIDERS,
    key: PATHS.BILLING_MANAGER_PROVIDERS,
    isProtected: true,
    page: <BILLING_MANAGER_PROVIDERS />,
  },
  {
    path: PATHS.VIEW_BILLING_MANAGER_PROVIDER,
    key: PATHS.VIEW_BILLING_MANAGER_PROVIDER,
    isProtected: true,
    page: <VIEW_BILLING_MANAGER_PROVIDER />,
  },
  {
    path: PATHS.VIEW_BILLING_MANAGER_PROVIDER_INVOICE,
    key: PATHS.VIEW_BILLING_MANAGER_PROVIDER_INVOICE,
    isProtected: true,
    page: <VIEW_BILLING_MANAGER_PROVIDER_INVOICE />,
  },
  {
    path: PATHS.BILLING_MANAGER_INVOICES,
    key: PATHS.BILLING_MANAGER_INVOICES,
    isProtected: true,
    page: <BILLING_MANAGER_INVOICES />,
  },
  {
    path: PATHS.VIEW_BILLING_MANAGER_INVOICE,
    key: PATHS.VIEW_BILLING_MANAGER_INVOICE,
    isProtected: true,
    page: <VIEW_BILLING_MANAGER_INVOICE />,
  },
  {
    path: PATHS.BILLING_MANAGER_REFUNDS,
    key: PATHS.BILLING_MANAGER_REFUNDS,
    isProtected: true,
    page: <BILLING_MANAGER_REFUNDS />,
  },
  {
    path: PATHS.VIEW_BILLING_MANAGER_REFUND,
    key: PATHS.VIEW_BILLING_MANAGER_REFUND,
    isProtected: true,
    page: <VIEW_BILLING_MANAGER_REFUND />,
  },
  {
    path: PATHS.PAYMENT_BILLING_MANAGER_INVOICE,
    key: PATHS.PAYMENT_BILLING_MANAGER_INVOICE,
    isProtected: true,
    page: <PAYMENT_BILLING_MANAGER_INVOICE />,
  },

  // Dietician

  {
    path: PATHS.DIETICIAN_DASHBOARD,
    key: PATHS.DIETICIAN_DASHBOARD,
    isProtected: true,
    page: <DIETICIAN_DASHBOARD />,
  },
  {
    path: PATHS.DIETICIAN_APPOINTMENTS,
    key: PATHS.DIETICIAN_APPOINTMENTS,
    isProtected: true,
    page: <DIETICIAN_APPOINTMENTS />,
  },
  {
    path: PATHS.VIEW_DIETICIAN_APPOINTMENT,
    key: PATHS.VIEW_DIETICIAN_APPOINTMENT,
    isProtected: true,
    page: <VIEW_DIETICIAN_APPOINTMENT />,
  },
  {
    path: PATHS.DIETICIAN_APPOINTMENT_DHP,
    key: PATHS.DIETICIAN_APPOINTMENT_DHP,
    isProtected: true,
    page: <DIETICIAN_APPOINTMENT_DHP />,
  },
  {
    path: PATHS.ADD_DIETICIAN_PRESCRIPTION,
    key: PATHS.ADD_DIETICIAN_PRESCRIPTION,
    isProtected: true,
    page: <ADD_DIETICIAN_PRESCRIPTION />,
  },
  {
    path: PATHS.VIEW_DIETICIAN_PRESCRIPTION,
    key: PATHS.VIEW_DIETICIAN_PRESCRIPTION,
    isProtected: true,
    page: <VIEW_DIETICIAN_PRESCRIPTION />,
  },
  {
    path: PATHS.DIETICIAN_PLANS,
    key: PATHS.DIETICIAN_PLANS,
    isProtected: true,
    page: <DIETICIAN_PLANS />,
  },
  {
    path: PATHS.VIEW_DIETICIAN_PLAN,
    key: PATHS.VIEW_DIETICIAN_PLAN,
    isProtected: true,
    page: <VIEW_DIETICIAN_PLAN />,
  },
  {
    path: PATHS.ADD_DIETICIAN_PLAN,
    key: PATHS.ADD_DIETICIAN_PLAN,
    isProtected: true,
    page: <ADD_DIETICIAN_PLAN />,
  },
  {
    path: PATHS.EDIT_DIETICIAN_PLAN,
    key: PATHS.EDIT_DIETICIAN_PLAN,
    isProtected: true,
    page: <EDIT_DIETICIAN_PLAN />,
  },
  {
    path: PATHS.DIETICIAN_IPDS,
    key: PATHS.DIETICIAN_IPDS,
    isProtected: true,
    page: <DIETICIAN_IPDS />,
  },
  {
    path: PATHS.DIETICIAN_IPD_VIEW,
    key: PATHS.DIETICIAN_IPD_VIEW,
    isProtected: true,
    page: <DIETICIAN_IPD_VIEW />,
  },
  {
    path: PATHS.DIETICIAN_IPD_DHP,
    key: PATHS.DIETICIAN_IPD_DHP,
    isProtected: true,
    page: <DIETICIAN_IPD_DHP />,
  },

  // Staff Manager
  {
    path: PATHS.STAFF_MANAGER_STAFF,
    key: PATHS.STAFF_MANAGER_STAFF,
    isProtected: true,
    page: <STAFF_MANAGER_STAFF_LIST />,
  },
  {
    path: PATHS.STAFF_MANAGER_STAFF_VIEW,
    key: PATHS.STAFF_MANAGER_STAFF_VIEW,
    isProtected: true,
    page: <STAFF_MANAGER_STAFF_VIEW />,
  },
  {
    path: PATHS.STAFF_MANAGER_ADD_STAFF,
    key: PATHS.STAFF_MANAGER_ADD_STAFF,
    isProtected: true,
    page: <STAFF_MANAGER_ADD_STAFF />,
  },
  {
    path: PATHS.STAFF_MANAGER_ADD_SLOTS,
    key: PATHS.STAFF_MANAGER_ADD_SLOTS,
    isProtected: true,
    page: <STAFF_MANAGER_ADD_SLOTS />,
  },
  {
    path: PATHS.STAFF_MANAGER_EDIT_SLOTS,
    key: PATHS.STAFF_MANAGER_EDIT_SLOTS,
    isProtected: true,
    page: <STAFF_MANAGER_EDIT_SLOTS />,
  },
  {
    path: PATHS.STAFF_MANAGER_EDIT_STAFF,
    key: PATHS.STAFF_MANAGER_EDIT_STAFF,
    isProtected: true,
    page: <STAFF_MANAGER_EDIT_STAFF />,
  },

  // Store Manager

  {
    path: PATHS.STORE_MANAGER_DASHBOARD,
    key: PATHS.STORE_MANAGER_DASHBOARD,
    isProtected: true,
    page: <STORE_MANAGER_DASHBOARD />,
  },
  {
    path: PATHS.STORE_MANAGER_INVENTORY,
    key: PATHS.STORE_MANAGER_INVENTORY,
    isProtected: true,
    page: <STORE_MANAGER_INVENTORY />,
  },
  {
    path: PATHS.STORE_MANAGER_INVENTORY_BULK_UPDATE,
    key: PATHS.STORE_MANAGER_INVENTORY_BULK_UPDATE,
    isProtected: true,
    page: <STORE_MANAGER_INVENTORY_BULK_UPDATE />,
  },
  {
    path: PATHS.VIEW_STORE_MANAGER_INVENTORY,
    key: PATHS.VIEW_STORE_MANAGER_INVENTORY,
    isProtected: true,
    page: <VIEW_STORE_MANAGER_INVENTORY />,
  },
  {
    path: PATHS.STORE_MANAGER_ASSIGNMENT_HISTORY,
    key: PATHS.STORE_MANAGER_ASSIGNMENT_HISTORY,
    isProtected: true,
    page: <STORE_MANAGER_ASSIGNMENT_HISTORY />,
  },
  {
    path: PATHS.STORE_MANAGER_REQUESTS,
    key: PATHS.STORE_MANAGER_REQUESTS,
    isProtected: true,
    page: <STORE_MANAGER_REQUESTS />,
  },
  {
    path: PATHS.VIEW_STORE_MANAGER_REQUEST,
    key: PATHS.VIEW_STORE_MANAGER_REQUEST,
    isProtected: true,
    page: <VIEW_STORE_MANAGER_REQUEST />,
  },
  {
    path: PATHS.STORE_MANAGER_PRODUCTS,
    key: PATHS.STORE_MANAGER_PRODUCTS,
    isProtected: true,
    page: <STORE_MANAGER_PRODUCTS />,
  },
  {
    path: PATHS.VIEW_STORE_MANAGER_PRODUCT,
    key: PATHS.VIEW_STORE_MANAGER_PRODUCT,
    isProtected: true,
    page: <VIEW_STORE_MANAGER_PRODUCT />,
  },
  {
    path: PATHS.ADD_STORE_MANAGER_PRODUCT,
    key: PATHS.ADD_STORE_MANAGER_PRODUCT,
    isProtected: true,
    page: <ADD_STORE_MANAGER_PRODUCT />,
  },
  {
    path: PATHS.EDIT_STORE_MANAGER_PRODUCT,
    key: PATHS.EDIT_STORE_MANAGER_PRODUCT,
    isProtected: true,
    page: <EDIT_STORE_MANAGER_PRODUCT />,
  },
  {
    path: PATHS.ADD_STORE_MANAGER_BULK_PRODUCT,
    key: PATHS.ADD_STORE_MANAGER_BULK_PRODUCT,
    isProtected: true,
    page: <ADD_STORE_MANAGER_BULK_PRODUCT />,
  },
  {
    path: PATHS.STORE_MANAGER_SERVICES,
    key: PATHS.STORE_MANAGER_SERVICES,
    isProtected: true,
    page: <STORE_MANAGER_SERVICES />,
  },
  {
    path: PATHS.VIEW_STORE_MANAGER_SERVICE,
    key: PATHS.VIEW_STORE_MANAGER_SERVICE,
    isProtected: true,
    page: <VIEW_STORE_MANAGER_SERVICE />,
  },
  {
    path: PATHS.ADD_STORE_MANAGER_SERVICE,
    key: PATHS.ADD_STORE_MANAGER_SERVICE,
    isProtected: true,
    page: <ADD_STORE_MANAGER_SERVICE />,
  },
  {
    path: PATHS.EDIT_STORE_MANAGER_SERVICE,
    key: PATHS.EDIT_STORE_MANAGER_SERVICE,
    isProtected: true,
    page: <EDIT_STORE_MANAGER_SERVICE />,
  },
  {
    path: PATHS.ADD_STORE_MANAGER_BULK_SERVICE,
    key: PATHS.ADD_STORE_MANAGER_BULK_SERVICE,
    isProtected: true,
    page: <ADD_STORE_MANAGER_BULK_SERVICE />,
  },

  // Admin Routes
  {
    path: PATHS.ADMIN_DASHBOARD,
    key: PATHS.ADMIN_DASHBOARD,
    isProtected: true,
    page: (
      <AdminGuard>
        <ADMIN_DASHBOARD />
      </AdminGuard>
    ),
  },
  {
    path: PATHS.ADMIN_STAKEHOLDERS,
    key: PATHS.ADMIN_STAKEHOLDERS,
    isProtected: true,
    page: (
      <AdminGuard>
        <ADMIN_STAKEHOLDERS />
      </AdminGuard>
    ),
  },
  {
    path: PATHS.ADMIN_MINUTES_WALLET,
    key: PATHS.ADMIN_MINUTES_WALLET,
    isProtected: true,
    page: (
      <AdminGuard>
        <ADMIN_MINUTES_WALLET />
      </AdminGuard>
    ),
  },
  {
    path: PATHS.ADMIN_COIN_MANAGEMENT,
    key: PATHS.ADMIN_COIN_MANAGEMENT,
    isProtected: true,
    page: (
      <AdminGuard>
        <ADMIN_COIN_MANAGEMENT />
      </AdminGuard>
    ),
  },
  {
    path: PATHS.ADMIN_COIN_MANAGEMENT_VIEW,
    key: PATHS.ADMIN_COIN_MANAGEMENT_VIEW,
    isProtected: true,
    page: (
      <AdminGuard>
        <ADMIN_COIN_MANAGEMENT_VIEW />
      </AdminGuard>
    ),
  },
];

export default routes;
